import { type HTMLAttributes, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorSpot5, Spacing12 } from 'autogen/design-tokens/tokens';
import type { CampaignInfo } from 'autogen/swagger/all';
import { CampaignMoreInfoAriaText } from 'autogen/translation-keys/trans-website-product';
import HighlightedText from 'components/generic/HighlightedText';
import { toHtmlContent } from 'modules/helpers/strings';
import Card from 'modules/shared/card';
import { useSetRecoilState } from 'recoil';
import campaignInfoAdditionalInfoIdAtom from '../recoil/additionalInfoIdAtom';
import AdditionalInfoButton from './AdditionalInfoButton';

const StyledCard = styled(Card)`
    display: flex;
    align-items: flex-start;
    border: 1px solid ${ColorSpot5};
`;

const TextContainer = styled(HighlightedText)<{ hasAdditionalInfo: CampaignInfo['hasAdditionalInfo'] }>`
    display: flex;
    padding: ${Spacing12} ${({ hasAdditionalInfo }) => (hasAdditionalInfo ? 0 : Spacing12)} ${Spacing12} ${Spacing12};
    width: 100%;
`;

const StyledAdditionalInfoButton = styled(AdditionalInfoButton)`
    padding: ${Spacing12};
    margin-left: auto;
`;

export interface ICampaignInfoBox extends HTMLAttributes<HTMLDivElement> {
    campaignInfo: CampaignInfo;
    productId: number;
}

export default function CampaignInfoBox({ campaignInfo, productId }: ICampaignInfoBox) {
    const { campaignTeaser, hasAdditionalInfo } = campaignInfo;
    const setProductId = useSetRecoilState(campaignInfoAdditionalInfoIdAtom);
    const { t } = useTranslation();

    useEffect(() => {
        setProductId(productId);
    }, []);

    if (!campaignTeaser) {
        return null;
    }

    return (
        <StyledCard>
            <TextContainer color="ColorSpot1" hasAdditionalInfo={hasAdditionalInfo} withBorder={false}>
                {toHtmlContent(campaignTeaser)}
            </TextContainer>
            {hasAdditionalInfo && <StyledAdditionalInfoButton aria-label={t(CampaignMoreInfoAriaText)} />}
        </StyledCard>
    );
}
